import { Currency } from 'domain/Currency/Currency'
import { PortOrAddress } from 'features/Directory'
import { TManualQuoteRequestStatus } from 'features/ManualQuotes'
import { ShipmentMode, ShipmentType } from 'features/ShipmentDetails'
import { compact, flatten, sumBy, values } from 'lodash-es'
import { isEUAddress, ISO } from 'service/ISO'

import { Cargo } from '../Cargo'
import { BookingParty } from '../Incoterms/incotermsMeta'
import { IQuoteServices } from '../QuoteServices'
import { getLineItemsSortedByType, IQuoteOption, optionValid, RateType, RouteQuote } from './QuoteSheetOption'
import { CarriageRate, Rate } from './Rate/Rate'

export type CustomsBondType = 'single' | 'annual' | 'own'

interface IResponseBase {
  isDemo?: boolean
  options: IQuoteOption[]
  option_ids: string[]
  assignees?: IAssignees
}

interface IAssignees {
  ops_id?: string
  sales_id?: string
}

interface IResponse extends IResponseBase {
  validUntil: Date
}

interface IResponseJSON extends IResponseBase {
  validUntil: string
}

interface IRequestBase {
  bookingId?: string
  customerId: string
  userId: string

  type: ShipmentType
  journey: {
    origin: PortOrAddress
    destination: PortOrAddress
  }
  modes: ShipmentMode[]
  manualQuoteRequest?: ManualQuoteRequest

  cargo: Cargo[]
  cargoCount: number
  goodsValue: Currency
  hideMainFAKRates?: boolean
  hideCarriageFAKRates?: boolean
  hideDepotFAKRates?: boolean
  hideMaerskSpotMainRates?: boolean
  totalWeight: number
  totalVolume: number

  // currencyExchangeRateId is mandatory from April 2020; set it to empty string if you don't require a specific currency exchange rate.
  currencyExchangeRateId: string

  services?: IQuoteServices
  isDemo?: boolean
  incoTerm: ISO.IncoTermsCode
  bookingParty: BookingParty
  isBookingPartyAgent?: boolean
}

interface IRequest extends IRequestBase {
  cargoReadyAt: Date
  ignorePreCarriageDuration: boolean
}

interface IRequestJSON extends IRequestBase {
  cargoReadyAt: string
}

interface IQuoteSheetCommon {
  _id: string
}

export interface IQuoteSheet extends IQuoteSheetCommon {
  createdAt: Date
  shipment_ids?: string[]
  request: IRequest
  response: IResponse
}

export interface IQuoteSheetJSON extends IQuoteSheetCommon {
  createdAt: string
  request: IRequestJSON
  response: IResponseJSON
}

interface ManualQuoteRequest {
  id: string
  status: TManualQuoteRequestStatus
  requestedAt: string
  requestedModes: ShipmentMode[]
}

export function fromJSON(quoteSheetJSON: IQuoteSheetJSON) {
  const ignorePreCarriageDuration = shouldIgnorePreCarriageDuration(quoteSheetJSON)

  const request: IRequest = {
    ...quoteSheetJSON.request,
    // @ts-expect-error TS(2322) FIXME: Type 'Date | null' is not assignable to type 'Date... Remove this comment to see the full error message
    cargoReadyAt: quoteSheetJSON.request.cargoReadyAt ? new Date(quoteSheetJSON.request.cargoReadyAt) : null,
    ignorePreCarriageDuration,
    services: quoteSheetJSON.request.services,
  }
  const response: IResponse = { ...quoteSheetJSON.response, validUntil: new Date(quoteSheetJSON.response.validUntil) }
  const quoteSheet: IQuoteSheet = {
    ...quoteSheetJSON,
    createdAt: new Date(quoteSheetJSON.createdAt),
    request,
    response,
  }

  quoteSheet.response.options = compact(
    quoteSheet.response.options.map((option, idx) => {
      if (!optionValid(option)) return null

      // we require to keep track of the original index because our booking-page is
      // index-based and would otherwise be broken during sorting / filtering
      option.internalIndex = idx
      option.mode = option.routeQuotes.mainQuote.mode as ShipmentMode
      option.type = quoteSheet.request.type

      // @ts-expect-error TS(2322) FIXME: Type '(RouteQuote | undefined)[]' is not assignabl... Remove this comment to see the full error message
      const routeQuotesForDurationCalculation: RouteQuote[] = ignorePreCarriageDuration
        ? [option.routeQuotes.mainQuote, option.routeQuotes.postQuote]
        : values<RouteQuote>(option.routeQuotes)

      option.totalDuration = sumBy(compact(routeQuotesForDurationCalculation), (route) => route.route.duration || 0)

      const routeQuotes = compact(values(option.routeQuotes))
      option.isNotFak = !isFak(routeQuotes)
      option.rateType = getRateType(routeQuotes)

      const quotes = compact([
        option.originCustomsQuote,
        option.vgmQuote,
        option.routeQuotes.preQuote,
        option.routeQuotes.mainQuote,
        option.routeQuotes.postQuote,
        option.destinationCustomsQuote,
        option.insuranceQuote,
        option.voucherQuote,
      ])

      option.lineItems = getLineItemsSortedByType(option)
      // @ts-expect-error TS(2345) FIXME: Argument of type '(aLineItem: LineItemInterface) =... Remove this comment to see the full error message
      option.totalEUR = sumBy(option.lineItems, (aLineItem) => aLineItem.totalEUR)
      option.additionalCharges = flatten(quotes.map((aQuote) => aQuote.additionalCharges || []))
      option.bookableUntil = new Date(option.bookableUntil)
      return option
    })
  )

  return quoteSheet
}

const isRateFAK = (rate: Rate | CarriageRate): boolean => {
  return rate.classification === 'FAK'
}

const isRateNAC = (rate: Rate | CarriageRate): boolean => {
  return rate.classification === 'NAC'
}

export const hasAllGeneralRates = (routeQuotes: RouteQuote[]): boolean => {
  return routeQuotes.every((quote) => quote.rates?.every(isRateFAK))
}

export const hasAllAgreedRates = (routeQuotes: RouteQuote[]): boolean =>
  routeQuotes.every((quote) => quote.rates?.every(isRateNAC))

export const hasHybridRates = (routeQuotes: RouteQuote[]): boolean => {
  if (hasAllAgreedRates(routeQuotes)) {
    return false
  }

  return routeQuotes.some((quote) => quote.rates?.some(isRateNAC))
}

const getRateType = (routeQuotes: RouteQuote[]): RateType | undefined => {
  if (hasAllAgreedRates(routeQuotes)) {
    return RateType.AGREED
  }

  if (hasHybridRates(routeQuotes)) {
    return RateType.HYBRID
  }

  if (hasAllGeneralRates(routeQuotes)) {
    return RateType.GENERAL
  }
}
export function isFak(routeQuotes: RouteQuote[]): boolean {
  // HOTFIX: some old shipments created from MQ doesn't have `rates` in RouteQuote
  // A QuoteOption is FAK if all its quotes have atleast one rate which is FAK
  return routeQuotes.every((quote) => quote.rates?.some(isRateFAK))
}

export function toJSON(quoteSheet: IQuoteSheet): IQuoteSheetJSON {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  return quoteSheet as any as IQuoteSheetJSON
}

export function isImportToEU(quoteSheet: IQuoteSheet | IQuoteSheetJSON): boolean {
  // @ts-expect-error TS(2322) FIXME: Type 'string | boolean | undefined' is not assigna... Remove this comment to see the full error message
  return isEUAddress(quoteSheet.request.journey.destination)
}

export function isExportFromEU(quoteSheet: IQuoteSheet | IQuoteSheetJSON): boolean {
  // @ts-expect-error TS(2322) FIXME: Type 'string | boolean | undefined' is not assigna... Remove this comment to see the full error message
  return isEUAddress(quoteSheet.request.journey.origin)
}

function shouldIgnorePreCarriageDuration(quoteSheet: IQuoteSheet | IQuoteSheetJSON): boolean {
  return !!(
    isExportFromEU(quoteSheet) &&
    quoteSheet.request.type === 'fcl' &&
    quoteSheet.request.modes.indexOf('sea') >= 0
  )
}
